<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="create-team-modal-overlay"
      @click.self="onClose"
    >
      <div
        class="create-team-modal"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
      >
        <button
          type="button"
          class="create-team-modal__close"
          :aria-label="$t('common.close')"
          @click="onClose"
        >
          <span aria-hidden="true">×</span>
        </button>
        <h2 :id="titleId" class="create-team-modal__title">{{ $t("survey.createNewTeam") }}</h2>
        <label class="create-team-modal__label" :for="inputId">{{ $t("survey.teamName") }}</label>
        <input
          :id="inputId"
          type="text"
          class="create-team-modal__input"
          :value="teamName"
          :placeholder="$t('survey.teamName')"
          autocomplete="organization"
          @input="onInput"
          @keyup.enter="onConfirm"
        />
        <div class="create-team-modal__actions">
          <button type="button" class="create-team-modal__btn create-team-modal__btn--secondary" @click="onClose">
            {{ $t("survey.cancel") }}
          </button>
          <button type="button" class="create-team-modal__btn create-team-modal__btn--primary" @click="onConfirm">
            {{ $t("survey.create") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
let _uid = 0;

export default {
  name: "CreateTeamModal",
  props: {
    modelValue: { type: Boolean, default: false },
    teamName: { type: String, default: "" },
  },
  emits: ["update:modelValue", "update:teamName", "confirm"],
  data() {
    _uid += 1;
    const id = `create-team-modal-${_uid}`;
    return { titleId: `${id}-title`, inputId: `${id}-input` };
  },
  methods: {
    onClose() {
      this.$emit("update:modelValue", false);
    },
    onInput(e) {
      this.$emit("update:teamName", e.target.value);
    },
    onConfirm() {
      this.$emit("confirm");
    },
  },
};
</script>

<style scoped>
.create-team-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(10, 20, 45, 0.42);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.create-team-modal {
  width: 100%;
  max-width: 420px;
  border-radius: 18px;
  border: 1px solid rgba(10, 20, 45, 0.1);
  background: linear-gradient(175deg, rgba(255, 255, 255, 0.98), rgba(246, 249, 255, 0.96));
  box-shadow: 0 24px 70px rgba(10, 20, 45, 0.16);
  padding: 24px 22px 22px;
  position: relative;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
}

.create-team-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(10, 20, 45, 0.06);
  color: rgba(10, 20, 45, 0.65);
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
}

.create-team-modal__close:hover {
  background: rgba(10, 20, 45, 0.1);
  color: rgba(10, 20, 45, 0.9);
}

.create-team-modal__title {
  margin: 0 40px 8px 0;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(10, 20, 45, 0.94);
  text-align: left;
  line-height: 1.25;
}

.create-team-modal__label {
  display: block;
  margin-top: 14px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(10, 20, 45, 0.55);
  text-align: left;
}

.create-team-modal__input {
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(10, 20, 45, 0.14);
  background: rgba(248, 250, 255, 0.98);
  font-size: 15px;
  font-weight: 500;
  color: rgba(10, 20, 45, 0.92);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.create-team-modal__input::placeholder {
  color: rgba(10, 20, 45, 0.38);
}

.create-team-modal__input:hover {
  border-color: rgba(32, 90, 255, 0.35);
}

.create-team-modal__input:focus {
  outline: none;
  border-color: rgba(32, 90, 255, 0.55);
  box-shadow: 0 0 0 4px rgba(32, 90, 255, 0.12);
}

.create-team-modal__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 22px;
}

.create-team-modal__btn {
  min-height: 42px;
  padding: 0 18px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.create-team-modal__btn--secondary {
  border: 1px solid rgba(10, 20, 45, 0.14);
  background: rgba(255, 255, 255, 0.9);
  color: rgba(10, 20, 45, 0.85);
}

.create-team-modal__btn--secondary:hover {
  border-color: rgba(10, 20, 45, 0.22);
  background: rgba(255, 255, 255, 1);
}

.create-team-modal__btn--primary {
  border: 1px solid rgba(32, 90, 255, 0.45);
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.95), rgba(0, 194, 255, 0.82));
  color: #fff;
  box-shadow: 0 8px 22px rgba(32, 90, 255, 0.22);
}

.create-team-modal__btn--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(32, 90, 255, 0.28);
}

.create-team-modal__btn:focus-visible {
  outline: 3px solid rgba(32, 90, 255, 0.45);
  outline-offset: 2px;
}
</style>
